import { Button, Chip } from "@heroui/react";
import HintTooltip from "@/components/common/HintTooltip";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { Eraser } from "lucide-react";

import InlineEditCell from "@/components/common/InlineEditCell";
import { formatAmount } from "@/lib/format";
import type { QuotationProcess, QuotationProcessInput } from "./api";

/**
 * 工程別内訳テーブルの 1 行。
 *
 * **工程マスタの全工程が 1 行ずつ並ぶ**。まだ金額を入れていない工程も 0 円の行
 * として出るので、`stored` が null の行がある（その工程の内訳はまだ保存されて
 * いない）。金額や時間を入れた時点で内訳の行が起こされ、`stored` が入る。
 */
export interface ProcessBreakdownRow {
  /** 表の行 id。工程ごとに一意（保存済みの行は内訳の id を混ぜる）。 */
  key: string;
  /** 表示上の行番号。工程マスタの並び（工程コード順）で 1..N。 */
  sequence: number;
  processId: number;
  processCode: string;
  processName: string;
  /** 使用停止になった工程。過去に入れた内訳だけが残っている行。 */
  processDisabled: boolean;
  /** 保存済みの内訳。**null はまだ何も入れていない工程**（0 円で出す）。 */
  stored: QuotationProcess | null;
}

/**
 * 工程別内訳テーブルの列定義。
 *
 * 画面の右半分に置くため、列幅の合計を 680px に収めてある。半分に割った
 * 1440px の画面でも横スクロールせずに操作列まで見えるようにするため。
 *
 * **行は工程マスタがそのまま決める**。使う工程を選んで積み上げるのではなく、
 * 登録されている工程を全部並べて、使う工程にだけ金額を入れる形にしてある
 * （[[ItemProcessPanel]]）。そのため並べ替えも行の追加も持たない。
 *
 * 金額の入れ方は 2 通りあり、**どちらで見積っている行かが見て分かる**ようにしてある。
 *
 * - **時間で見積る行** … 時間と時間単価が入り、金額はその積。金額は結果なので
 *   直接は編集できない（編集できるように見せると、時間と食い違う額を入れられる）
 * - **金額を直接入れる行** … 時間が空で、金額を打つ。外注費のように時間に
 *   馴染まない工程で使う
 *
 * 時間を空にすると「金額を直接入力」に、時間を入れると「時間で見積る」に切り替わる。
 * 切り替えはサーバー側で金額を計算し直すので、ここでは値を送るだけでよい。
 *
 * 工程名は編集できない（行＝工程なので、別の工程にしたいなら別の行に入れる）。
 */
export interface QuotationProcessColumnHandlers {
  canWrite: boolean;
  /** 1 項目だけ保存する。エラー文言を返すとセルの値は元に戻る。 */
  onSaveField: (
    row: ProcessBreakdownRow,
    input: QuotationProcessInput,
  ) => Promise<string | null>;
  /** その工程の入力を取り消す（内訳の行を消して 0 円に戻す）。 */
  onClear: (row: ProcessBreakdownRow) => void;
}

const helper = createColumnHelper<ProcessBreakdownRow>();

export function buildQuotationProcessColumns({
  canWrite,
  onSaveField,
  onClear,
}: QuotationProcessColumnHandlers): ColumnDef<ProcessBreakdownRow, never>[] {
  return [
    helper.accessor("sequence", {
      id: "sequence",
      header: "行",
      size: 50,
      meta: { label: "行番号" },
      cell: (ctx) => <span className="text-default-500">{ctx.getValue()}</span>,
    }),
    helper.accessor("processName", {
      id: "process_name",
      header: "工程",
      size: 150,
      meta: { label: "工程" },
      cell: (ctx) => {
        const row = ctx.row.original;
        return (
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate" title={`${row.processCode} ${row.processName}`}>
              {row.processName}
            </span>
            {/* 使用停止になった工程でも、過去の見積はそのまま残る。 */}
            {row.processDisabled && (
              <Chip size="sm" variant="flat" color="danger" className="shrink-0">
                停止
              </Chip>
            )}
          </div>
        );
      },
    }),
    helper.display({
      id: "hours",
      header: "時間",
      size: 80,
      meta: { label: "所要時間", numeric: true },
      // 空にすると「金額を直接入力」に切り替わる。
      cell: (ctx) => {
        const row = ctx.row.original;
        return (
          <InlineEditCell
            label="所要時間"
            type="number"
            numeric
            value={row.stored?.hours ?? null}
            canWrite={canWrite}
            onSave={(v) => onSaveField(row, { hours: v })}
          />
        );
      },
    }),
    helper.display({
      id: "hourly_price",
      header: "時間単価",
      size: 90,
      meta: { label: "時間単価", numeric: true },
      cell: (ctx) => {
        const row = ctx.row.original;
        // 金額を直接入れる行（と、まだ何も入れていない行）は時間単価を持たない。
        if (row.stored?.hours == null) {
          return <span className="text-default-300">—</span>;
        }
        const value = row.stored.hourly_price;
        return (
          <InlineEditCell
            label="時間単価"
            type="number"
            numeric
            value={value}
            displayValue={value ? formatAmount(value) : null}
            canWrite={canWrite}
            onSave={(v) => onSaveField(row, { hourly_price: v })}
          />
        );
      },
    }),
    helper.display({
      id: "price",
      header: "金額",
      size: 100,
      meta: { label: "金額（1 個あたり）", numeric: true },
      cell: (ctx) => {
        const row = ctx.row.original;
        // 時間で見積った行の金額は 時間 × 時間単価 の結果なので編集させない。
        if (row.stored?.hours != null) {
          return (
            <span className="text-default-500" title="時間 × 時間単価">
              {formatAmount(row.stored.price)}
            </span>
          );
        }
        // まだ何も入れていない工程は 0 円。ここに金額を入れると内訳が起こる。
        const price = row.stored?.price ?? "0";
        return (
          <InlineEditCell
            label="金額"
            type="number"
            numeric
            value={price}
            displayValue={formatAmount(price)}
            canWrite={canWrite}
            onSave={(v) => onSaveField(row, { price: v ?? "0" })}
          />
        );
      },
    }),
    helper.display({
      id: "note",
      header: "摘要",
      size: 140,
      meta: { label: "摘要" },
      cell: (ctx) => {
        const row = ctx.row.original;
        return (
          <InlineEditCell
            label="摘要"
            value={row.stored?.note ?? null}
            canWrite={canWrite}
            onSave={(v) => onSaveField(row, { note: v })}
          />
        );
      },
    }),
    helper.display({
      id: "actions",
      header: "操作",
      size: 70,
      enableResizing: false,
      meta: { label: "操作" },
      cell: (ctx) => {
        const row = ctx.row.original;
        // 行は消えない（工程マスタが行を決めるため）。消すのは入れた中身だけで、
        // 取り消すとその工程は 0 円に戻る。何も入れていない行では押せない。
        const canClear = canWrite && row.stored != null;
        return (
          <div className="flex items-center gap-0.5">
            <HintTooltip
              content={
                !canWrite
                  ? "リーダー以上の権限が必要です"
                  : row.stored == null
                    ? "まだ何も入力されていません"
                    : "入力を取り消す（0 円に戻す）"
              }
            >
              {/* 無効なボタンはイベントを出さないため、Tooltip 用に包む。 */}
              <span className="inline-flex">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  color="danger"
                  aria-label={`${row.processName}の入力を取り消す`}
                  isDisabled={!canClear}
                  onPress={() => onClear(row)}
                >
                  <Eraser size={16} />
                </Button>
              </span>
            </HintTooltip>
          </div>
        );
      },
    }),
  ] as ColumnDef<ProcessBreakdownRow, never>[];
}
